import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, IconButton, Divider, FormInstance, Input, Textarea} from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import PageContent from '@/components/PageContent';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import { AxiosError } from 'axios';
import { AdvanceShareStyleVersion, AdvanceShareStyleVersionStatusEnum, AdvanceShareStyleVersionStatusList, UpsertAdvanceShareStyleVersion } from '@/models/advance-share-style-version.model';
import { upsertAdvanceShareStyleVersion } from '@/services/advance-share-style-version.service';
import FetchingSelect from '@/components/FetchingSelect';
import { getAdvanceShareStylesLookup } from '@/services/advance-share-style.service';
import AssetDefinitionPanel from './style-asset-definition-panel';
import ErrorMessage from '@/components/ErrorMessage';
import VariableDefinitionPanel from './style-variable-definition-panel';
import RenderingConfigDefinitionPanel from './style-rendering-config-definition-panel';

const schema = Schema.Model({
  id: Schema.Types.NumberType(),
  styleId: Schema.Types.NumberType().isRequired('This field is required.'),
  promptTemplate: Schema.Types.StringType().isRequired('This field is required.'),
  assets: Schema.Types.ArrayType(),
  variables: Schema.Types.ArrayType(),
  policies: Schema.Types.ArrayType(),
  defaultAspectRatio: Schema.Types.NumberType(),
  preferredAiProvider: Schema.Types.NumberType()

});

const emptyModel: UpsertAdvanceShareStyleVersion = {
  id: undefined,
  styleId: undefined,
  promptTemplate: undefined,
  assets: undefined,
  variables: undefined,
  policies: undefined,
  renderingConfig: undefined,
};

interface AdvanceShareStyleVersionFormProps{
  styleId?: number,
  formObject?: AdvanceShareStyleVersion,
  pageState: PageState,
};


const AdvanceShareStyleVersionForm: React.FC<AdvanceShareStyleVersionFormProps> = ({formObject, pageState, styleId}) => {
  const [formValue, setFormValue] = useState<UpsertAdvanceShareStyleVersion>(emptyModel);
  const [formError, setFormError] = useState<Record<string, any>>({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [isDisable, setIsDisable] = useState(false);
  
  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    setIsDisable(!!formObject && formObject.status != AdvanceShareStyleVersionStatusEnum.Draft);

    if(pageState == PageState.New){
      setFormValue({...emptyModel, styleId});
      return;
    }

    if (formObject) {
      const castedModel: UpsertAdvanceShareStyleVersion = { 
        ...formObject, 
      };

      setFormValue(castedModel);
    } else {
      setFormValue(emptyModel);
    }
  }, [formObject]);

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      console.log("errors:", formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<any> = await upsertAdvanceShareStyleVersion(formValue);
      if (result.success) {
        nav('/advance-shares/styles/' + formValue.styleId, {state:{tab: "style-versions"}}); 
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  return (
    <PageContent>
     <Form name='advance-share-style-form' fluid
        ref={formRef}
        onChange={handleFormChange}
        onCheck={setFormError}
        formValue={formValue}
        model={schema}
        disabled={loading || isDisable}
      >
        <Grid w='100%' fluid >
          <Row justifyContent="center">
            <Col span={{xs:24, xl:12}} >
              <Row>
                <Col span={{xs:24}} >
                  <Form.Group>
                    <Form.Label>Style</Form.Label>
                    <Form.Control name="styleId" accepter={FetchingSelect} block
                        fetchData={(text, page) => getAdvanceShareStylesLookup(text, page)} 
                        placeholder="Select a style"
                        disabled
                    />                 
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col span={{xs:24, xl:12}} >
                  <Form.Group>
                    <Form.Label>Version</Form.Label>
                    <Input value={formObject?.version} disabled/>                   
                  </Form.Group>     
                </Col>
                <Col span={{xs:24, xl:12}} >
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Input value={AdvanceShareStyleVersionStatusList.findLast(s => s.value == formObject?.status)?.label} disabled/>                   
                  </Form.Group>    
                </Col>
              </Row>
              <Row>
                <Col span={{xs:24}} >              
                  <Form.Group>
                    <Form.Label>Prompt Template</Form.Label>
                    <Form.Control 
                      name="promptTemplate" 
                      value={formValue.promptTemplate ?? ''}
                        accepter={Textarea} 
                        rows = {30}
                      />
                  </Form.Group>
                </Col>
              </Row>
            </Col>       
            <Col span={{xs:24, xl:12}} >
              
              <Row>
                <Col span={{xs:24}} >
                  <AssetDefinitionPanel 
                    disabled={isDisable}
                    value={formValue.assets ?? []}
                    onChange= {val => setFormValue(pre => ({...pre, assets: val }))}
                  />
                </Col>
              </Row>
              <Row>
                <Col span={{xs:24}} >
                  <VariableDefinitionPanel 
                    disabled={isDisable}
                    value={formValue.variables ?? []}
                    onChange= {val => setFormValue(pre => ({...pre, variables: val }))}
                  />
                </Col>
              </Row>
              <Row>
                <Col span={{xs:24}} >
                  <RenderingConfigDefinitionPanel 
                    disabled={isDisable}
                    value={formValue.renderingConfig}
                    onChange= {val => setFormValue(pre => ({...pre, renderingConfig: val }))}
                  />
                </Col>
              </Row>

            </Col>
          </Row>
        </Grid>
      </Form>

      <Divider />
      {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}

      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
          Back
        </IconButton>
        <IconButton
          icon={<PageNextIcon />}
          placement="right"
          appearance="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </IconButton>
      </Stack>
    </PageContent>
  );
};

export default AdvanceShareStyleVersionForm;

